/**
 * WeekendSaleBanner – Saturday Flash Sale (11 AM – 11 PM IST)
 * 100% identical UI to notescrafter-print HomePage banner.
 */
import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Sale constants ────────────────────────────────────────────────────────────
const SALE_BW_ORIGINAL = 1.25;
const SALE_BW_PRICE = 0.79;
const SALE_COLOR_ORIGINAL = 3.35;
const SALE_COLOR_PRICE = 1.89;

/** Returns the next Saturday sale window start/end in UTC
 *  11:00 AM IST = 05:30 UTC  |  11:00 PM IST = 17:30 UTC
 */
function getNextSaleWindow(): { start: Date; end: Date } {
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  const nowIST = new Date(Date.now() + IST_OFFSET_MS);
  const dayOfWeekIST = nowIST.getUTCDay(); // 0=Sun … 6=Sat
  const daysUntilSat = dayOfWeekIST === 6 ? 0 : (6 - dayOfWeekIST);

  const satIST = new Date(nowIST);
  satIST.setUTCDate(nowIST.getUTCDate() + daysUntilSat);
  const satYear = satIST.getUTCFullYear();
  const satMonth = satIST.getUTCMonth();
  const satDay = satIST.getUTCDate();

  // 11:00 AM IST on Saturday = 05:30 UTC
  const start = new Date(Date.UTC(satYear, satMonth, satDay, 5, 30, 0, 0));
  // 11:00 PM IST on Saturday = 17:30 UTC
  const end = new Date(Date.UTC(satYear, satMonth, satDay, 17, 30, 0, 0));
  return { start, end };
}

function useSaleCountdown() {
  const [saleActive, setSaleActive] = useState(false);
  const [saleUpcoming, setSaleUpcoming] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ h: 0, m: 0, s: 0 });
  const [dismissed, setDismissed] = useState(false);

  const tick = useCallback(() => {
    const now = Date.now();
    const { start, end } = getNextSaleWindow();

    if (now >= start.getTime() && now < end.getTime()) {
      setSaleActive(true); setSaleUpcoming(false);
      const diff = end.getTime() - now;
      setTimeLeft({ h: Math.floor(diff / 3_600_000), m: Math.floor((diff % 3_600_000) / 60_000), s: Math.floor((diff % 60_000) / 1_000) });
    } else if (now < start.getTime()) {
      setSaleActive(false); setSaleUpcoming(true);
      const diff = start.getTime() - now;
      setTimeLeft({ h: Math.floor(diff / 3_600_000), m: Math.floor((diff % 3_600_000) / 60_000), s: Math.floor((diff % 60_000) / 1_000) });
    } else {
      setSaleActive(false); setSaleUpcoming(false);
    }
  }, []);

  useEffect(() => { tick(); const id = setInterval(tick, 1000); return () => clearInterval(id); }, [tick]);
  return { saleActive, saleUpcoming, timeLeft, dismissed, setDismissed };
}

// ─── ZapIcon (inline to avoid import drift) ───────────────────────────────────
function ZapIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" />
    </svg>
  );
}

// ─── WeekendSaleBanner ────────────────────────────────────────────────────────
export default function WeekendSaleBanner() {
  const { saleActive, timeLeft, dismissed, setDismissed } = useSaleCountdown();
  
  const { start, end } = getNextSaleWindow();
  const now = Date.now();
  const visibilityStart = start.getTime() - 24 * 60 * 60 * 1000;
  const visibilityEnd = end.getTime();
  const visible = (now >= visibilityStart && now < visibilityEnd) && !dismissed;
  
  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="weekend-sale"
          initial={{ opacity: 0, y: -24, scale: 0.97 }}
          animate={{ 
            opacity: 1, 
            y: 0, 
            scale: 1,
            boxShadow: saleActive 
              ? [
                  '0 0 40px rgba(16,185,129,0.14), 0 0 80px rgba(5,150,105,0.08)', 
                  '0 0 60px rgba(16,185,129,0.28), 0 0 100px rgba(52,211,153,0.16)', 
                  '0 0 40px rgba(16,185,129,0.14), 0 0 80px rgba(5,150,105,0.08)'
                ]
              : '0 0 40px rgba(16,185,129,0.14), 0 0 80px rgba(5,150,105,0.08)'
          }}
          exit={{ opacity: 0, y: -16, scale: 0.96 }}
          transition={{ 
            boxShadow: {
              repeat: Infinity,
              duration: 2,
              ease: "easeInOut"
            },
            default: {
              duration: 0.55, 
              ease: [0.22, 1, 0.36, 1]
            }
          }}
          className="relative w-full mb-6 overflow-hidden rounded-2xl"
          style={{
            background: 'linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(5,150,105,0.10) 40%, rgba(52,211,153,0.10) 100%)',
            border: '1px solid rgba(16,185,129,0.30)',
          }}
        >
          {/* Animated glow sweep */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'linear-gradient(100deg, transparent 0%, rgba(255,255,255,0.04) 50%, transparent 100%)' }}
            animate={{ x: ['-100%', '200%'] }}
            transition={{ repeat: Infinity, duration: 3.5, ease: 'linear', repeatDelay: 1.5 }}
          />

          {/* Dismiss button */}
          <button
            onClick={() => setDismissed(true)}
            className="absolute top-3 right-3 z-20 w-6 h-6 rounded-full bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 border border-black/10 dark:border-white/10 flex items-center justify-center text-text-muted hover:text-text-main transition-all"
            aria-label="Dismiss sale banner"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="relative z-10 px-5 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">

            {/* Left: Badge + label */}
            <div className="flex items-center gap-3">
              <div className="relative shrink-0">
                <motion.div
                  animate={{ scale: [1, 1.15, 1], opacity: [0.7, 1, 0.7] }}
                  transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}
                  className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm"
                  style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.7), rgba(5,150,105,0.6))' }}
                >
                  <ZapIcon className="w-4 h-4 text-white" />
                </motion.div>
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-[0.22em] font-black text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
                  {saleActive && (
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                  )}
                  {saleActive ? '⚡ Weekend Flash Sale · Live Now' : '⏰ Upcoming · Saturday Flash Sale'}
                </span>
                <span className="text-base sm:text-lg font-black text-text-main leading-tight mt-0.5">
                  Lowest Prices of All Time
                </span>
                <span className="text-[11px] text-text-muted font-medium mt-0.5">
                  Every Saturday · 11 AM – 11 PM IST
                </span>
              </div>
            </div>

            {/* Center: Prices */}
            <div className="flex items-center gap-5 shrink-0">
              {/* B&W */}
              <div className="flex flex-col items-center">
                <span className="text-[9px] uppercase tracking-widest font-bold text-text-muted mb-0.5">B&amp;W / page</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-bold text-text-muted/60 line-through decoration-emerald-500">₹{SALE_BW_ORIGINAL.toFixed(2)}</span>
                  <motion.span
                    animate={{ opacity: saleActive ? [0.7, 1, 0.7] : 1 }}
                    transition={{ repeat: saleActive ? Infinity : 0, duration: 1.8, ease: 'easeInOut' }}
                    className="text-xl sm:text-2xl font-black text-emerald-600 dark:text-emerald-400"
                    style={{ textShadow: saleActive ? '0 0 18px rgba(16,185,129,0.5)' : 'none' }}
                  >
                    ₹{SALE_BW_PRICE.toFixed(2)}
                  </motion.span>
                </div>
              </div>

              <div className="w-px h-10 bg-black/10 dark:bg-white/10" />

              {/* Color */}
              <div className="flex flex-col items-center">
                <span className="text-[9px] uppercase tracking-widest font-bold text-text-muted mb-0.5">Color / page</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-bold text-text-muted/60 line-through decoration-emerald-500">₹{SALE_COLOR_ORIGINAL.toFixed(2)}</span>
                  <motion.span
                    animate={{ opacity: saleActive ? [0.7, 1, 0.7] : 1 }}
                    transition={{ repeat: saleActive ? Infinity : 0, duration: 1.8, ease: 'easeInOut', delay: 0.3 }}
                    className="text-xl sm:text-2xl font-black text-emerald-600 dark:text-emerald-400"
                    style={{ textShadow: saleActive ? '0 0 18px rgba(52,211,153,0.5)' : 'none' }}
                  >
                    ₹{SALE_COLOR_PRICE.toFixed(2)}
                  </motion.span>
                </div>
              </div>
            </div>

            {/* Right: Countdown */}
            <div className="flex flex-col items-center shrink-0">
              <span className="text-[9px] uppercase tracking-widest font-black text-text-muted mb-2">
                {saleActive ? 'Ends in' : 'Starts in'}
              </span>
              <div className="flex items-center gap-1.5">
                {[pad(timeLeft.h), pad(timeLeft.m), pad(timeLeft.s)].map((val, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <div
                      className="flex flex-col items-center px-2.5 py-1.5 rounded-lg bg-white/80 dark:bg-black/40 border border-black/10 dark:border-white/10 shadow-sm"
                      style={{
                        minWidth: '2.6rem',
                      }}
                    >
                      <motion.span
                        key={val}
                        initial={{ y: -8, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        className="text-xl sm:text-2xl font-black text-text-main font-mono leading-none"
                      >
                        {val}
                      </motion.span>
                      <span className="text-[8px] text-text-muted font-bold uppercase tracking-widest mt-0.5">
                        {['HR', 'MIN', 'SEC'][i]}
                      </span>
                    </div>
                    {i < 2 && <span className="text-lg font-black text-text-muted/40 -mt-3">:</span>}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

